/**
 * "Active chart" tracking (owner 2026-09-28, Q-G3: the window-wide top toolbar and the right
 * sidebar's watchlist drive the ACTIVE chart). Pure, so the rules are unit-tested:
 *
 * - The active chart is the chart module the user **last interacted with** (pointer down or focus
 *   anywhere in it, in any window — pop-outs share this JS context). Inside a split-chart module
 *   the module's own active pane (chartLayout.ts `active`) is the target, so "active chart" =
 *   last-used module × its active pane.
 * - Charts that mount do not steal activity; the first chart to mount becomes active only when
 *   there is none (a fresh workspace has a target at once).
 * - When the active chart unmounts (closed, workspace switch), the most recently used remaining
 *   chart becomes active; with none left there is no target (the toolbar says so).
 * - Listeners hear about a change of the active chart and about state changes *of* the active
 *   chart (symbol, timeframe, pane), never about background charts.
 */
export class ActiveChartTracker<C> {
  /** Most recently used first. */
  private readonly mru: string[] = [];
  private readonly charts = new Map<string, C>();
  private readonly listeners = new Set<() => void>();
  private rev = 0;

  register(id: string, chart: C): void {
    const known = this.charts.has(id);
    this.charts.set(id, chart);
    if (known) return;
    const wasEmpty = this.mru.length === 0;
    this.mru.push(id);
    if (wasEmpty) this.emit();
  }

  unregister(id: string): void {
    if (!this.charts.delete(id)) return;
    const i = this.mru.indexOf(id);
    if (i >= 0) this.mru.splice(i, 1);
    if (i === 0) this.emit();
  }

  /** The user used this chart: it becomes the active one. */
  activate(id: string): void {
    if (!this.charts.has(id)) return;
    const i = this.mru.indexOf(id);
    if (i === 0) return;
    if (i > 0) this.mru.splice(i, 1);
    this.mru.unshift(id);
    this.emit();
  }

  /** A chart's own state changed; listeners hear it only when that chart is the active one. */
  changed(id: string): void {
    if (this.mru[0] === id) this.emit();
  }

  get activeId(): string | null {
    return this.mru[0] ?? null;
  }

  get active(): C | null {
    const id = this.mru[0];
    return id === undefined ? null : (this.charts.get(id) ?? null);
  }

  get size(): number {
    return this.charts.size;
  }

  /** Bumps on every notification (useSyncExternalStore snapshot). */
  get revision(): number {
    return this.rev;
  }

  subscribe = (l: () => void): (() => void) => {
    this.listeners.add(l);
    return () => {
      this.listeners.delete(l);
    };
  };

  private emit(): void {
    this.rev++;
    for (const l of this.listeners) l();
  }
}
